import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/common/DataTable';
import { LabeledSelectBox, type DateRange } from '@/components/ui';
import { formatTime, formatDateTimeShort } from '@/utils/date';
import { useTranslation } from 'react-i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { useTheme } from '@/theme';
import {
  TabLayout,
  TabHeader,
  ErrorMessage,
  EmptyState,
  LoadingSkeleton,
  DownloadButtons,
} from '@/components/layout/TabLayout';
import * as XLSX from 'xlsx';

import type {
  DatalogTabProps,
  SensorData,
  FlatMiniData,
  FlatSensorData,
  GasReadingData,
} from './datalog/datalogTabTypes';
import { createChartOption } from './datalog/datalogChartOptions';
import { useGasReadings } from './datalog/useGasReadings';
import { DeviceType } from '@packages/types/device/common/deviceTypes';
import {
  buildMiniDatalogColumns,
  buildLiteDatalogColumns,
  toPlainRow,
  type DatalogColumnSpec,
} from './datalog/datalogColumnSpec';
import { useNotificationStore } from '@/store/notificationStore';

export const DatalogTab: React.FC<DatalogTabProps> = ({
  device,
  deviceType = DeviceType.LITE,
  onExportData,
  onDownloadPdf,
}) => {
  const { t } = useTranslation('datalog');
  const { theme } = useTheme();
  const { showError } = useNotificationStore();
  const chartRef = useRef<any>(null);

  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    return {
      from: sevenDaysAgo.toISOString().split('T')[0],
      to: today.toISOString().split('T')[0],
    };
  });

  const [selectedSensor, setSelectedSensor] = useState<string | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const {
    gasReadings,
    loadingData,
    error,
    sensorDataMap,
    sensorOptions,
    miniHasTwaStel,
    getLiteHasTwaStel,
    getIsLelSensor,
    fetchAllForExcel,
    excelLoading,
  } = useGasReadings({ device, deviceType, dateRange });

  const liteHasTwaStel = useMemo(
    () => getLiteHasTwaStel(selectedSensor),
    [selectedSensor, getLiteHasTwaStel]
  );

  const isLelSensor = useMemo(
    () => getIsLelSensor(selectedSensor),
    [selectedSensor, getIsLelSensor]
  );

  const isMultiDayRange = useMemo(() => {
    if (!dateRange.from || !dateRange.to) return false;
    return dateRange.from !== dateRange.to;
  }, [dateRange.from, dateRange.to]);

  // Lite: 첫 번째 센서 자동 선택
  useEffect(() => {
    if (deviceType === DeviceType.LITE && sensorOptions.length > 0 && !selectedSensor) {
      setSelectedSensor(sensorOptions[0].value as string);
    }
  }, [deviceType, sensorOptions, selectedSensor]);

  // ==================== Flatten helpers ====================

  // LEL 센서 전압 포매팅: mV → V 변환, raw 0이면 3.00V (이전 FW)
  const formatLelVolt = (mV: number | null | undefined): string => {
    if (mV == null) return '-';
    return (mV / 1000).toFixed(2);
  };

  // GasReadingData(다중 센서)에서 선택된 센서만 뽑아 SensorData 형태로 변환 (Excel export용)
  const extractSensorData = (data: GasReadingData[], sensorName: string): SensorData[] => {
    return data
      .filter(r => r.sensors?.some(s => s.sensor_name === sensorName))
      .map(r => {
        const sensor = r.sensors!.find(s => s.sensor_name === sensorName)!;
        return {
          timestamp: r.timestamp,
          temperature: r.temperature,
          battery_volt: r.battery_volt,
          lel_sensor_volt: r.lel_sensor_volt,
          system_status: r.system_status,
          system_status_name: r.system_status_name,
          gas_conc: sensor.gas_conc,
          stel_conc: sensor.stel_conc,
          twa_conc: sensor.twa_conc,
          unit_name: sensor.unit_name,
          sensor_name: sensor.sensor_name,
          decimal: sensor.decimal,
          sensor_status: sensor.sensor_status,
          sensor_status_name: sensor.sensor_status_name,
        };
      });
  };

  const flattenSensorData = (data: SensorData[]): FlatSensorData[] => {
    return data.map(reading => {
      const dp = reading.decimal ?? 0;
      return {
        timestamp: reading.timestamp,
        time_formatted: isMultiDayRange
          ? formatDateTimeShort(reading.timestamp)
          : formatTime(reading.timestamp),
        gas_conc: reading.gas_conc,
        gas_conc_formatted: reading.gas_conc?.toFixed(dp) ?? '-',
        stel_conc: reading.stel_conc,
        stel_conc_formatted: reading.stel_conc?.toFixed(dp) ?? '-',
        twa_conc: reading.twa_conc,
        twa_conc_formatted: reading.twa_conc?.toFixed(dp) ?? '-',
        temperature: reading.temperature,
        temperature_formatted: reading.temperature?.toFixed(1) ?? '-',
        battery_volt: reading.battery_volt,
        battery_volt_formatted: reading.battery_volt?.toFixed(2) ?? '-',
        lel_sensor_volt: reading.lel_sensor_volt,
        lel_sensor_volt_formatted: formatLelVolt(reading.lel_sensor_volt),
        system_status_name: reading.system_status_name,
        sensor_status: reading.sensor_status,
        sensor_status_name: reading.sensor_status_name,
        unit_name: reading.unit_name,
      };
    });
  };

  const flattenMiniData = (data: GasReadingData[]): FlatMiniData[] => {
    return data.map(reading => ({
      timestamp: reading.timestamp,
      time_formatted: isMultiDayRange
        ? formatDateTimeShort(reading.timestamp)
        : formatTime(reading.timestamp),
      gas_conc: reading.gas_conc ?? 0,
      gas_conc_formatted: reading.gas_conc?.toFixed(2) ?? '-',
      stel_conc: reading.stel_conc ?? 0,
      stel_conc_formatted: reading.stel_conc?.toFixed(2) ?? '-',
      twa_conc: reading.twa_conc ?? 0,
      twa_conc_formatted: reading.twa_conc?.toFixed(2) ?? '-',
      temperature: reading.temperature,
      temperature_formatted: reading.temperature?.toFixed(1) ?? '-',
      battery_volt: reading.battery_volt,
      battery_volt_formatted: reading.battery_volt?.toFixed(2) ?? '-',
      lel_sensor_volt: reading.lel_sensor_volt,
      lel_sensor_volt_formatted: formatLelVolt(reading.lel_sensor_volt),
      system_status_name: reading.system_status_name,
    }));
  };

  const flatMiniData = useMemo(() => flattenMiniData(gasReadings), [gasReadings, isMultiDayRange]);

  // ==================== Chart options ====================

  const miniChartOption = useMemo(() => {
    if (deviceType !== DeviceType.MINI || gasReadings.length === 0) return {};
    return createChartOption({
      data: gasReadings,
      unitName: 'ppm',
      isMultiDayRange,
      theme,
    });
  }, [gasReadings, deviceType, isMultiDayRange, theme]);

  const createSensorChart = (sensorName: string, data: SensorData[]) => {
    const unitName = data.length > 0 ? data[0].unit_name : 'ppm';
    return createChartOption({
      data,
      unitName,
      isMultiDayRange,
      title: sensorName,
      theme,
    });
  };

  // ==================== Column definitions ====================
  // 테이블/PDF/Excel 세 출력이 동일한 컬럼 스펙을 공유 (datalogColumnSpec.ts)

  const miniColumnSpecs = useMemo(
    () => buildMiniDatalogColumns(t(i18nKeys.datalog.time), miniHasTwaStel),
    [t, miniHasTwaStel]
  );

  const liteColumnSpecs = useMemo((): DatalogColumnSpec<FlatSensorData>[] => {
    if (deviceType !== DeviceType.LITE || !selectedSensor || !sensorDataMap.has(selectedSensor))
      return [];

    const sensorData = sensorDataMap.get(selectedSensor)!;
    const unitName = sensorData[0]?.unit_name || 'ppm';
    return buildLiteDatalogColumns(t(i18nKeys.datalog.time), unitName, liteHasTwaStel, isLelSensor);
  }, [deviceType, selectedSensor, sensorDataMap, liteHasTwaStel, isLelSensor, t]);

  const miniColumns = useMemo(
    (): ColumnDef<FlatMiniData>[] =>
      miniColumnSpecs.map(spec => ({ accessorKey: spec.accessorKey, header: spec.header })),
    [miniColumnSpecs]
  );

  const liteSensorColumns = useMemo(
    (): ColumnDef<FlatSensorData>[] =>
      liteColumnSpecs.map(spec => ({ accessorKey: spec.accessorKey, header: spec.header })),
    [liteColumnSpecs]
  );

  // ==================== Export data ====================

  const onExportDataRef = useRef(onExportData);
  useEffect(() => {
    onExportDataRef.current = onExportData;
  }, [onExportData]);

  useEffect(() => {
    if (!onExportDataRef.current || loadingData) return;

    const buildTableData = () => {
      if (deviceType === DeviceType.MINI && gasReadings.length > 0) {
        const tableData = flatMiniData.map(row => toPlainRow(row, miniColumnSpecs));
        const tableColumns = miniColumnSpecs.map(spec => ({
          header: spec.header,
          accessorKey: spec.header,
        }));
        return { tableData, tableColumns };
      }

      if (deviceType === DeviceType.LITE && selectedSensor && sensorDataMap.has(selectedSensor)) {
        const sensorData = sensorDataMap.get(selectedSensor)!;
        const flattened = flattenSensorData(sensorData);
        const tableData = flattened.map(row => toPlainRow(row, liteColumnSpecs));
        const tableColumns = liteColumnSpecs.map(spec => ({
          header: spec.header,
          accessorKey: spec.header,
        }));
        return { tableData, tableColumns };
      }

      return { tableData: [], tableColumns: [] };
    };

    const hasChart =
      (deviceType === DeviceType.MINI && gasReadings.length > 0) ||
      (deviceType === DeviceType.LITE && selectedSensor && sensorDataMap.has(selectedSensor));

    if (!hasChart) {
      const { tableData, tableColumns } = buildTableData();
      onExportDataRef.current({ chartImageUrl: undefined, tableData, tableColumns });
      return;
    }

    const prepareExportData = async () => {
      let chartImageUrl: string | undefined;

      if (chartRef.current) {
        try {
          await new Promise(resolve => setTimeout(resolve, 1000));
          // await 동안 언마운트/차트 제거로 ref가 null이 될 수 있으므로 재확인
          const echartsInstance = chartRef.current?.getEchartsInstance();
          if (
            echartsInstance &&
            echartsInstance.getWidth() > 0 &&
            echartsInstance.getHeight() > 0
          ) {
            chartImageUrl = echartsInstance.getDataURL({
              type: 'png',
              pixelRatio: 2,
              backgroundColor: '#ffffff',
            });
          }
        } catch (e) {
          console.error('Failed to get chart image:', e);
        }
      }

      const { tableData, tableColumns } = buildTableData();
      onExportDataRef.current?.({ chartImageUrl, tableData, tableColumns });
    };

    const timer = setTimeout(prepareExportData, 1000);
    return () => clearTimeout(timer);
  }, [
    deviceType,
    gasReadings,
    flatMiniData,
    selectedSensor,
    sensorDataMap,
    loadingData,
    miniColumnSpecs,
    liteColumnSpecs,
    isMultiDayRange,
  ]);

  // ==================== Row click -> chart zoom ====================

  const handleRowClick = (timestamp: string) => {
    if (!chartRef.current) return;
    const echartsInstance = chartRef.current.getEchartsInstance();
    if (!echartsInstance) return;

    const currentData =
      deviceType === DeviceType.MINI
        ? gasReadings
        : selectedSensor && sensorDataMap.has(selectedSensor)
          ? sensorDataMap.get(selectedSensor)!
          : [];

    if (currentData.length === 0) return;

    const timestamps = currentData.map(d => new Date(d.timestamp).getTime());
    const minTime = Math.min(...timestamps);
    const maxTime = Math.max(...timestamps);
    const clickedTime = new Date(timestamp).getTime();
    const range = maxTime - minTime;
    const viewRange = range * 0.1;

    let startTime = clickedTime - viewRange / 2;
    let endTime = clickedTime + viewRange / 2;

    if (startTime < minTime) {
      startTime = minTime;
      endTime = Math.min(minTime + viewRange, maxTime);
    }
    if (endTime > maxTime) {
      endTime = maxTime;
      startTime = Math.max(maxTime - viewRange, minTime);
    }

    echartsInstance.dispatchAction({ type: 'dataZoom', startValue: startTime, endValue: endTime });
  };

  const handleExcelDownload = async () => {
    try {
      const allData = await fetchAllForExcel();
      if (allData.length === 0) return;

      let rows: Record<string, unknown>[] = [];

      if (deviceType === DeviceType.MINI) {
        rows = flattenMiniData(allData).map(row => toPlainRow(row, miniColumnSpecs));
      } else if (selectedSensor) {
        const sensorData = extractSensorData(allData, selectedSensor);
        rows = flattenSensorData(sensorData).map(row => toPlainRow(row, liteColumnSpecs));
      }

      if (rows.length === 0) return;

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

      const sensorSuffix =
        deviceType === DeviceType.LITE && selectedSensor ? `_${selectedSensor}` : '';
      const fileName = `datalog_${device.serial_number}${sensorSuffix}_${dateRange.from}_${dateRange.to}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (e) {
      console.error('Excel export failed:', e);
      showError(i18nKeys.datalog.excelGenerationFailed, i18nKeys.datalog.excelGenerationFailed);
    }
  };

  // ==================== Render ====================

  const renderChartAndTable = (chartOption: any, tableData: any[], columns: any[]) => (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing[4],
        width: '100%',
      }}
    >
      <div
        style={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          borderRadius: theme.borderRadius.md,
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing[5],
          }}
        >
          <div
            style={{
              width: '100%',
              borderRadius: theme.borderRadius.md,
              border: `1px solid ${theme.colors.border.container}`,
              flexShrink: 0,
            }}
          >
            <div
              style={{
                width: '100%',
                height: '400px',
                position: 'relative',
                maxWidth: '100%',
                overflow: 'hidden',
              }}
            >
              <ReactECharts
                ref={chartRef}
                option={chartOption}
                style={{ height: '100%', width: '100%', maxWidth: '100%' }}
                opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
              />
            </div>
          </div>
          <div
            style={{
              width: '100%',
              borderRadius: theme.borderRadius.md,
              flexShrink: 0,
            }}
          >
            <DataTable
              data={tableData}
              columns={columns}
              enablePagination={true}
              pageSize={20}
              pageSizeOptions={[10, 20, 50, 100]}
              enableSorting={true}
              emptyMessage={t(i18nKeys.datalog.noDataMessage)}
              enableExcelExport={false}
              enableColumnVisibility={true}
              columnVisibility={columnVisibility}
              onColumnVisibilityChange={updater => {
                const newVis = typeof updater === 'function' ? updater(columnVisibility) : updater;
                setColumnVisibility(newVis);
              }}
              onRowClick={row => handleRowClick(row.timestamp)}
            />
          </div>
        </div>
      </div>
      <DownloadButtons
        onDownloadExcel={handleExcelDownload}
        onDownloadPdf={onDownloadPdf}
        disabled={loadingData || excelLoading || gasReadings.length === 0}
      />
    </div>
  );

  return (
    <TabLayout>
      <TabHeader
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        sensorSelect={
          deviceType === DeviceType.LITE && sensorOptions.length > 0 ? (
            <LabeledSelectBox
              label="Data"
              value={selectedSensor || ''}
              options={sensorOptions}
              onChange={option => setSelectedSensor(option.value as string)}
              isEdit={true}
              width="190px"
              labelWidth="50px"
              size="sm"
              labelSize="xs"
            />
          ) : undefined
        }
      />

      {error && !loadingData && <ErrorMessage message={error} />}

      {loadingData ? (
        <LoadingSkeleton count={10} showChart={true} />
      ) : gasReadings.length === 0 ? (
        <EmptyState message="No data in selected period" />
      ) : deviceType === DeviceType.MINI ? (
        renderChartAndTable(miniChartOption, flatMiniData, miniColumns)
      ) : selectedSensor && sensorDataMap.has(selectedSensor) ? (
        renderChartAndTable(
          createSensorChart(selectedSensor, sensorDataMap.get(selectedSensor)!),
          flattenSensorData(sensorDataMap.get(selectedSensor)!),
          liteSensorColumns
        )
      ) : (
        <EmptyState message={t(i18nKeys.datalog.pleaseSelectSensor)} />
      )}
    </TabLayout>
  );
};
